<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Filtros (una sola línea) -->
        <b-row align-v="end">
          <!-- Per Page -->
          <b-col
            cols="auto"
            class="d-flex align-items-center mb-1 mb-md-0"
          >
            <label class="mb-0 mr-50">Mostrar</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block"
            />
            <label class="mb-0 ml-50">entradas</label>
          </b-col>

          <!-- Departamento -->
          <b-col>
            <label>Departamento</label>
            <v-select
              v-model="filterDepartamento"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="departamentos"
              :reduce="(d) => d.key"
              label="label"
              :clearable="true"
              placeholder="Todos"
            />
          </b-col>

          <!-- Rango de fechas -->
          <b-col>
            <label>Rango de fechas de entrega</label>
            <div class="d-flex align-items-center">
              <flat-pickr
                v-model="rangeDate"
                class="form-control"
                placeholder="Selecciona un rango de fechas"
                :config="{
                  mode: 'range',
                  showMonths: 2,
                  format: 'd-m-Y',
                  altFormat: 'd-m-Y',
                  altInput: true,
                  locale: Spanish,
                }"
                @on-change="onRangeChange"
              />
              <b-button
                v-if="rangeDate"
                variant="outline-danger"
                class="btn-icon ml-1"
                @click="clearRange"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
          </b-col>

          <!-- Search -->
          <b-col>
            <label>Buscar</label>
            <b-form-input
              v-model="searchQuery"
              placeholder="Buscar..."
            />
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refHistoricoMaterialesListTable"
        class="position-relative"
        :items="fetchHistoricoMateriales"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No se encontraron registros coincidentes"
        :sort-desc.sync="isSortDirDesc"
        :busy="!fetchHistoricoMateriales"
      >
        <!-- Table Loading -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" small />
            <strong class="ml-1">Cargando datos...</strong>
          </div>
        </template>

        <!-- Column: Departamento -->
        <template #cell(departamento)="data">
          <span>{{ data.item.departamento || '-' }}</span>
        </template>

        <!-- Column: Fecha -->
        <template #cell(delivery_date)="data">
          <span v-if="data.item.delivery_date">{{ formatDate(data.item.delivery_date) }}</span>
          <span v-else>-</span>
        </template>

        <!-- Column: Nº Pedido -->
        <template #cell(pedido_num)="data">
          <router-link
            target="_blank"
            style="padding: 6px 0 0 6px"
            :to="{ name: 'albaran-edit', params: { id: data.item.fk_albaran } }"
          >
            <slot>{{ data.item.pedido_num || 'Entrega directa' }}
              <feather-icon
                icon="ExternalLinkIcon"
                size="16"
              />
            </slot>
          </router-link>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <!-- See Pedido -->
          <b-button
            :id="`edit-${data.item.id}`"
            @click="seeAlbaran(data.item.fk_albaran)"
            variant="success"
            class="btn-icon mr-1 mb-1"
          >
            <feather-icon icon="EyeIcon" />
          </b-button>
          <b-tooltip
            :target="`edit-${data.item.pedido_id}`"
            triggers="hover"
            placement="bottom"
            boundary="window"
          >
            <b>Ver entrega</b>
          </b-tooltip>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de
              {{ dataMeta.of }} entradas</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalHistoricoMateriales"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BLink,
  BBadge,
  BPagination,
  BSpinner,
  BTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { Spanish } from "flatpickr/dist/l10n/es";
import moment from "moment";
import store from "@/store";
import { onUnmounted, ref } from "@vue/composition-api";
import useDepartamentosList from "@/views/system/departamento/useDepartamentosList";
import departamentoStoreModule from "@/views/system/departamento/departamentoStoreModule";
import useHistoricoMaterialesList from "./useHistoricoMaterialesList";
import historicoMaterialesStoreModule from "./historicoMaterialesStoreModule";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BLink,
    BBadge,
    BPagination,
    BSpinner,
    BTooltip,

    vSelect,
    flatPickr,
  },
  data() {
    return {
      deleteModal: false,
      selectedItem: {},
      Spanish,
      rangeDate: null,
    };
  },
  methods: {
    seeAlbaran(id) {
      this.$router.push({
        name: "albaran-edit",
        params: {
          id,
        },
      });
    },
    // flatpickr en modo range emite selectedDates: filtramos solo cuando hay rango completo (2)
    // o cuando se limpia (0). Con una sola fecha seleccionada esperamos a la segunda.
    onRangeChange(selectedDates) {
      if (selectedDates.length === 2) {
        this.fechaDesde = moment(selectedDates[0]).format("YYYY-MM-DD");
        this.fechaHasta = moment(selectedDates[1]).format("YYYY-MM-DD");
      } else if (selectedDates.length === 0) {
        this.fechaDesde = null;
        this.fechaHasta = null;
      }
    },
    clearRange() {
      this.rangeDate = null;
      this.fechaDesde = null;
      this.fechaHasta = null;
    },
  },
  mounted() {
    // Restaurar el rango visible del picker si la URL traía fechas (useQuerySync ya
    // rellenó fechaDesde/fechaHasta; el flatpickr usa rangeDate, hay que repoblarlo).
    if (this.fechaDesde && this.fechaHasta) {
      this.rangeDate = [this.fechaDesde, this.fechaHasta];
    }
  },
  setup() {
    const HISTORICO_MATERIALES_APP_STORE_MODULE_NAME =
      "app-historico-materiales";
    const DEPARTAMENTO_APP_STORE_MODULE_NAME = "app-departamento";

    // Register module
    if (!store.hasModule(HISTORICO_MATERIALES_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        HISTORICO_MATERIALES_APP_STORE_MODULE_NAME,
        historicoMaterialesStoreModule
      );
    }

    if (!store.hasModule(DEPARTAMENTO_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        DEPARTAMENTO_APP_STORE_MODULE_NAME,
        departamentoStoreModule
      );
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(HISTORICO_MATERIALES_APP_STORE_MODULE_NAME))
        store.unregisterModule(HISTORICO_MATERIALES_APP_STORE_MODULE_NAME);

      if (store.hasModule(DEPARTAMENTO_APP_STORE_MODULE_NAME))
        store.unregisterModule(DEPARTAMENTO_APP_STORE_MODULE_NAME);
    });

    const {
      fetchHistoricoMateriales,
      tableColumns,
      perPage,
      currentPage,
      totalHistoricoMateriales,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refHistoricoMaterialesListTable,
      filterDepartamento,
      fechaDesde,
      fechaHasta,
      refetchData,
    } = useHistoricoMaterialesList();

    const departamentos = ref([]);
    const { listDepartamentos } = useDepartamentosList();
    listDepartamentos().then((response) => {
      departamentos.value = response;
    });

    return {
      fetchHistoricoMateriales,
      tableColumns,
      perPage,
      currentPage,
      totalHistoricoMateriales,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refHistoricoMaterialesListTable,
      filterDepartamento,
      fechaDesde,
      fechaHasta,
      departamentos,
      refetchData,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
